// Updated PasswordSecurity.tsx to support external submit
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '@/components/common/Input';
import { usersApi } from '@/api/endpoints/users';
import toast from 'react-hot-toast';

interface PasswordSecurityProps {
  onSetSubmitCallback?: (callback: (() => void) | null) => void;
}

export const PasswordSecurity: React.FC<PasswordSecurityProps> = ({ onSetSubmitCallback }) => {
  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    }
  });

  const newPassword = watch('newPassword');

  const onSubmit = async (data: any) => {
    try {
      await usersApi.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      toast.success('Password updated successfully');
      reset();
    } catch (error) {
      toast.error('Failed to update password');
    }
  };

  useEffect(() => {
    if (onSetSubmitCallback) {
      onSetSubmitCallback(() => handleSubmit(onSubmit));
    }
    return () => {
      if (onSetSubmitCallback) {
        onSetSubmitCallback(null);
      }
    };
  }, [onSetSubmitCallback, handleSubmit]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-3">
          <h2 className="text-xl font-medium text-gray-900">Update Password</h2>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">
            For security reasons, we recommend updating your password regularly. Make sure it's strong and unique.
          </p>
        </div>
        
        <form id="password-form" onSubmit={handleSubmit(onSubmit)} className="col-span-9">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password
              </label>
              <Input
                type="password"
                {...register('currentPassword', { required: 'Current password is required' })}
                error={errors.currentPassword?.message}
                placeholder="••••••••••••"
                className="w-full"
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter your existing password to verify your identity before making changes.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <Input
                type="password"
                {...register('newPassword', { 
                  required: 'New password is required',
                  minLength: { value: 8, message: 'Password must be at least 8 characters' }
                })}
                error={errors.newPassword?.message}
                placeholder="Create a strong password"
                className="w-full"
              />
              <p className="mt-1 text-xs text-gray-500">
                Set a new password that's at least 8 characters long and includes a mix of letters, numbers, and symbols.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <Input
                type="password"
                {...register('confirmPassword', { 
                  required: 'Please confirm your password',
                  validate: value => value === newPassword || 'Passwords do not match'
                })}
                error={errors.confirmPassword?.message}
                placeholder="Re-enter new password"
                className="w-full"
              />
              <p className="mt-1 text-xs text-gray-500">
                Re-enter your new password to confirm and avoid typos.
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};